import type { ReactElement } from "react"
import { useEffect, useState } from "react"
import ButtonAtm from "@onestore/hel/dist/components/atoms/ButtonAtm"
import SelectButtonAtm from "@onestore/hel/dist/components/atoms/SelectButtonAtm"
import TextAtm from "@onestore/hel/dist/components/atoms/TextAtm"
import AdvancedProductTilesOrg from "@onestore/hel/dist/components/organisms/AdvancedProductTilesOrg"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import BreakpointVisibilityOrg from "@onestore/hel/dist/components/organisms/BreakpointVisibilityOrg"
import FlexContainerOrg from "@onestore/hel/dist/components/organisms/FlexContainerOrg"
import FlexItemOrg from "@onestore/hel/dist/components/organisms/FlexItemOrg"
import HorizontalScrollItemsOrg from "@onestore/hel/dist/components/organisms/HorizontalScrollItemsOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import WrapOrg from "@onestore/hel/dist/components/organisms/WrapOrg"
import _t from "@core/i18n"
import { getFormattedPeriodInfo } from "@core/period-info"
import { getCurrencyCode } from "@core/pricing"
import { PriceFormat } from "@core/types"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import Header from "@gatsby-plugin-generic-page/components/Header"
import type { AdvancedDomainItemsSectionFlatData } from "@gatsby-plugin-generic-page/fragments/advancedDomainItemsSection"
import useDomainExtensionsByCategories from "@gatsby-plugin-onestore/hooks/useDomainExtensionsByCategories"
import useMenuQuery from "@gatsby-plugin-onestore/hooks/useMenuQuery"
import { useLocation } from "@reach/router"
import { get, trimStart } from "lodash"
import { Helmet } from "react-helmet"
import { helmetJsonLdProp } from "react-schemaorg"
import type { AggregateOffer, Offer, Product, WithContext } from "schema-dts"
import TogglePriceType from "~/components/TogglePriceType"
import { useSessionStorage } from "~/hooks/useSessionStorage"
import useSiteMetadataQuery from "~/hooks/useSiteMetadataQuery"
import { sendContentShowEvent } from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"
import type { DomainExtensionCategory } from "~/types/squidex"
import AdvancedDomainItem from "./AdvancedDomainItem"

type DomainSchemaItem = {
  name: string
  price: string
  url: string
  image: string
}

function createSingleDomainContext(
  item: DomainSchemaItem,
  brandName: string
): WithContext<Product> {
  const offers: Offer = {
    "@type": "Offer",
    url: item.url,
    priceCurrency: getCurrencyCode(),
    price: item.price,
    availability: "https://schema.org/InStock",
    itemCondition: "https://schema.org/NewCondition",
    seller: {
      "@type": "Organization",
      name: brandName,
    },
  }

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: item.name,
    url: item.url,
    ...(item.image ? { image: item.image } : {}),
    brand: {
      "@type": "Brand",
      name: brandName,
    },
    offers,
  }
}

function createAggregateDomainContext(
  items: DomainSchemaItem[],
  pageUrl: string,
  brandName: string,
  logoUrl: string
): WithContext<Product> {
  const lowPrice = items.reduce((min, item) =>
    parseFloat(item.price) < parseFloat(min.price) ? item : min
  ).price

  const offers: AggregateOffer = {
    "@type": "AggregateOffer",
    url: pageUrl,
    priceCurrency: getCurrencyCode(),
    lowPrice,
    availability: "https://schema.org/InStock",
  }

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: _t("basket.productType.domain"),
    url: pageUrl,
    ...(logoUrl ? { image: logoUrl } : {}),
    brand: {
      "@type": "Brand",
      name: brandName,
    },
    offers,
  }
}

const getCategoryIndex = (domainData: DomainExtensionCategory[]): number => {
  const categoryFromUrl = trimStart(window.location.hash, "#")

  return domainData.findIndex(
    (category) => category.flatData.slug === categoryFromUrl
  )
}

function AdvancedDomainItemsSection({
  activeColor,
  sectionSettings,
  hasTogglePriceType = false,
  header,
}: AdvancedDomainItemsSectionFlatData): ReactElement<AdvancedDomainItemsSectionFlatData> {
  const domainData = useDomainExtensionsByCategories()
  const location = useLocation()
  const site = useSiteMetadataQuery()
  const { menu } = useMenuQuery()
  const fallbackLogoUrl: string = get(menu, "flatData.logo[0].url", "")
  const pageUrl = `${site.siteUrl}${location.pathname}`

  useEffect(() => {
    if (!location.hash) {
      return
    }

    const categoryIndex = getCategoryIndex(domainData)

    if (categoryIndex === -1) {
      return
    }

    setActiveDesktopDomainsIndex(categoryIndex)
  }, [])

  const [isMobileDomainsLimited, setIsMobileDomainsLimited] = useState(true)
  const [activeStoredDesktopDomainsIndex, setActiveDesktopDomainsIndex] =
    useSessionStorage<number>("advanced-domain-page-items-tab", 0)

  const activeDesktopDomainsIndex =
    activeStoredDesktopDomainsIndex >= domainData.length
      ? 0
      : activeStoredDesktopDomainsIndex

  const [isExpanded, setExpanded] = useSessionStorage<boolean>(
    "advanced-domain-page-items-button",
    false
  )

  const domainSchemaItems = domainData.flatMap((category) =>
    category.flatData.domains
      .filter((domain) => !isEmpty(domain.flatData.pages))
      .map((domain) => {
        const { title, extension, periods, pages, logo } = domain.flatData
        const name = title || `.${extension}`
        const price = getFormattedPeriodInfo(
          periods,
          undefined,
          PriceFormat.REGISTER_NETTO_DOT_SEPARATED
        )
        const image = !isEmpty(logo) ? logo![0].url : fallbackLogoUrl

        return {
          name,
          price,
          url: `${site.siteUrl}${pages[0].flatData.url}`,
          image,
        }
      })
      .filter(({ price }) => price !== "")
  )

  return (
    <>
      {!isEmpty(domainSchemaItems) ? (
        <Helmet
          script={[
            helmetJsonLdProp(
              domainSchemaItems.length === 1
                ? createSingleDomainContext(
                    domainSchemaItems[0],
                    site.ogSiteName
                  )
                : createAggregateDomainContext(
                    domainSchemaItems,
                    pageUrl,
                    site.ogSiteName,
                    fallbackLogoUrl
                  )
            ),
          ]}
        />
      ) : null}

      <BaseSection {...sectionSettings}>
        <BaseContainer {...sectionSettings?.containerSettings}>
          {header ? (
            <PushOrg bottomSpace={8}>
              <AlignContentOrg
                horizontalAlign={{
                  small: "left",
                  medium: "center",
                  large: "center",
                }}
              >
                <Header {...header} />
              </AlignContentOrg>
            </PushOrg>
          ) : null}

          <BreakpointVisibilityOrg from="small" to="large">
            {domainData.map((item, index) =>
              isMobileDomainsLimited && index >= 2 ? null : (
                <PushOrg bottomSpace={3} key={item.flatData.name}>
                  <TextAtm typography="big3" pushSpace={2}>
                    {item.flatData.name}
                  </TextAtm>

                  <HorizontalScrollItemsOrg
                    gutterSpace={1.5}
                    itemWidthSpace={37}
                  >
                    {item.flatData.domains
                      .filter((domain) => !isEmpty(domain.flatData.pages))
                      .map((domain) => (
                        <AdvancedDomainItem
                          planId={domain.flatData.id}
                          label={domain.flatData.label}
                          extension={domain.flatData.extension}
                          periods={domain.flatData.periods}
                          description={domain.flatData.description}
                          title={domain.flatData.title}
                          link={
                            isEmpty(domain.flatData.pages)
                              ? undefined
                              : domain.flatData.pages[0].flatData.url
                          }
                          key={`${item.flatData.name}/${domain.flatData.extension}`}
                          logo={
                            isEmpty(domain.flatData.logo)
                              ? undefined
                              : {
                                  src: domain.flatData.logo![0].url,
                                  alt:
                                    domain.flatData.logo![0].alt ??
                                    `${_t("domains.domain")} .${domain.flatData.extension}`,
                                  width: domain.flatData.logo![0].width,
                                  height: domain.flatData.logo![0].height,
                                }
                          }
                        />
                      ))}
                  </HorizontalScrollItemsOrg>
                </PushOrg>
              )
            )}

            {isMobileDomainsLimited ? (
              <AlignContentOrg horizontalAlign="center">
                <ButtonAtm
                  variant="outlined"
                  color="secondary"
                  title={_t("domains.showNextCategory")}
                  onClick={() => {
                    sendContentShowEvent()
                    window.jentisDataLayer?.pushContentShowEvent()
                    setIsMobileDomainsLimited(false)
                  }}
                >
                  {_t("domains.showNextCategory")}
                </ButtonAtm>
              </AlignContentOrg>
            ) : null}
          </BreakpointVisibilityOrg>

          <BreakpointVisibilityOrg from="large">
            <PushOrg bottomSpace={5}>
              <FlexContainerOrg
                justifyContent="space-between"
                alignItems="center"
              >
                <FlexItemOrg flexGrow={1} flexShrink={1} flexBasis={0}>
                  <WrapOrg gutterSpace={1} data-test-id="domain-ext-categories">
                    {domainData.map((item, index) => (
                      <SelectButtonAtm
                        onClick={() => setActiveDesktopDomainsIndex(index)}
                        isActive={index === activeDesktopDomainsIndex}
                        activeColor={
                          !isEmpty(activeColor) ? activeColor : undefined
                        }
                        key={item.flatData.name}
                      >
                        {item.flatData.name}
                      </SelectButtonAtm>
                    ))}
                  </WrapOrg>
                </FlexItemOrg>

                {hasTogglePriceType ? (
                  <PushOrg leftSpace={3}>
                    <TogglePriceType />
                  </PushOrg>
                ) : null}
              </FlexContainerOrg>
            </PushOrg>

            {domainData.map((item, categoryIndex) => {
              const maxNumberOfDomains = 8
              const hasTooManyDomains =
                item.flatData.domains.length > maxNumberOfDomains

              return (
                <div
                  style={
                    activeDesktopDomainsIndex !== categoryIndex
                      ? {
                          display: "none",
                        }
                      : undefined
                  }
                  key={item.flatData.name}
                >
                  <AdvancedProductTilesOrg columns={4} gutterSpace={2.5}>
                    {item.flatData.domains
                      .filter((domain) => !isEmpty(domain.flatData.pages))
                      .map((domain, domainIndex) => (
                        <div
                          style={{
                            display:
                              hasTooManyDomains &&
                              !isExpanded &&
                              domainIndex >= maxNumberOfDomains
                                ? "none"
                                : "grid",
                          }}
                          key={`${item.flatData.name}/${domain.flatData.extension}`}
                        >
                          <AdvancedDomainItem
                            planId={domain.flatData.id}
                            label={domain.flatData.label}
                            extension={domain.flatData.extension}
                            title={domain.flatData.title}
                            periods={domain.flatData.periods}
                            description={domain.flatData.description}
                            link={
                              isEmpty(domain.flatData.pages)
                                ? undefined
                                : domain.flatData.pages[0].flatData.url
                            }
                            logo={
                              isEmpty(domain.flatData.logo)
                                ? undefined
                                : {
                                    src: domain.flatData.logo![0].url,
                                    alt:
                                      domain.flatData.logo![0].alt ??
                                      `${_t("domains.domain")} .${domain.flatData.extension}`,
                                    width: domain.flatData.logo![0].width,
                                    height: domain.flatData.logo![0].height,
                                  }
                            }
                          />
                        </div>
                      ))}
                  </AdvancedProductTilesOrg>

                  {hasTooManyDomains && !isExpanded ? (
                    <PushOrg topSpace={5}>
                      <AlignContentOrg horizontalAlign="center">
                        <ButtonAtm
                          variant="outlined"
                          color="secondary"
                          title={_t("domains.showNextDomains")}
                          onClick={() => {
                            sendContentShowEvent()
                            window.jentisDataLayer?.pushContentShowEvent()
                            setExpanded(true)
                          }}
                        >
                          {_t("domains.showNextDomains")}
                        </ButtonAtm>
                      </AlignContentOrg>
                    </PushOrg>
                  ) : null}
                </div>
              )
            })}
          </BreakpointVisibilityOrg>
        </BaseContainer>
      </BaseSection>
    </>
  )
}

export default AdvancedDomainItemsSection
